import type { TranslationKeys } from '@genome/i18n/src/i18n/translations';

export interface AppProfile {
  id: string;
  name: string;
  icon: string; // lucide icon name or emoji
  description: string;
  systemPrompt: string;
  contextText: string;
  // Termini di dominio per il boosting Deepgram: il profilo generico ha lista
  // vuota (trascrizione neutra), i tecnici potenziano i termini IT, gli altri
  // i termini del proprio settore. Vuoto/undefined = nessun boost.
  keyterms?: string[];
  // Domini del dizionario tecnico (Task #13): abilitano i keyterms dinamici
  // (seed + learned validato). Assente/vuoto = solo la lista statica sopra.
  dictionaryDomains?: string[];
  isCustom?: boolean;
}

// Set di termini per dominio. Tenuti come fonte unica: aggiornarli qui si
// riflette sul boosting di trascrizione di tutti i profili che li usano.
const TECH_KEYTERMS = [
  'React', 'Frontend', 'Backend', 'Node.js', 'TypeScript', 'JavaScript',
  'Next.js', 'Redux', 'GraphQL', 'AWS', 'Kubernetes', 'Docker', 'Cloudflare',
  'API', 'REST', 'Database', 'SQL', 'NoSQL', 'Microservizi', 'Serverless',
  'Angular', 'Vue'
];
const DATA_KEYTERMS = [
  'machine learning', 'dataset', 'modello', 'feature', 'training', 'Python',
  'pandas', 'SQL', 'algoritmo', 'regressione', 'classificazione', 'neural network'
];
const CYBER_KEYTERMS = [
  'vulnerabilità', 'exploit', 'firewall', 'SIEM', 'incident response',
  'compliance', 'penetration test', 'malware', 'zero trust', 'CVE'
];
const SALES_KEYTERMS = [
  'pipeline', 'closing', 'obiezione', 'upselling', 'CRM', 'ROI', 'lead',
  'funnel', 'budget', 'contratto'
];
const HR_KEYTERMS = [
  'onboarding', 'turnover', 'recruiting', 'soft skill', 'colloquio', 'RAL',
  'benefit', 'competenze'
];
const MARKETING_KEYTERMS = [
  'funnel', 'conversione', 'CTR', 'ROAS', 'brand', 'lead', 'campagna', 'SEO',
  'engagement', 'budget'
];
const DESIGN_KEYTERMS = [
  'wireframe', 'usability', 'Figma', 'design system', 'prototipo', 'UX',
  'accessibilità', 'user research'
];
const PM_KEYTERMS = [
  'roadmap', 'backlog', 'sprint', 'stakeholder', 'OKR', 'milestone',
  'retrospettiva', 'KPI', 'MVP'
];
const CONSULTANT_KEYTERMS = [
  ...TECH_KEYTERMS, 'architettura', 'solution', 'cloud', 'migrazione',
  'scalabilità', 'SLA', 'TCO'
];

// Costruisce un prompt professionale distintivo per i profili non-dev: stesse
// regole JSON/formato del generico ma con ruolo, focus e avvisi di dominio.
function buildProfilePrompt(role: string, focus: string): string {
  return `Sei un assistente AI professionale specializzato come ${role}. Il tuo obiettivo è supportare l'utente come teleprompter intelligente durante riunioni, call e colloqui.

REGOLA FONDAMENTALE:
Rispondi in modo chiaro, conciso e discorsivo, con taglio professionale da ${role}. ${focus} Non generare blocchi di codice a meno che non sia esplicitamente richiesto dall'utente.

STRUTTURA DI OUTPUT OBBLIGATORIA (Puro Testo Markdown):
Non usare formati JSON. Rispondi direttamente con il testo, rispettando esattamente i seguenti blocchi:
- **Risposta Pronta:** un'introduzione parlata di 2-3 frasi, diretta e adatta a un contesto ${role}.
- **Punti chiave:** elenco puntato chiaro con i concetti principali del dominio (senza codice).
`;
}

const GENERIC_PROMPT = `Sei un Assistente AI Professionale. Il tuo obiettivo è supportare l'utente fungendo da teleprompter intelligente durante riunioni, conversazioni o interviste generali.

REGOLA FONDAMENTALE:
Rispondi sempre in modo chiaro, conciso e discorsivo. Non sei uno sviluppatore e non devi generare blocchi di codice a meno che non sia esplicitamente richiesto dall'utente.

STRUTTURA DI OUTPUT OBBLIGATORIA (Puro Testo Markdown):
Non usare formati JSON. Rispondi direttamente con il testo, rispettando esattamente i seguenti blocchi:
- **Risposta Pronta:** Un'introduzione parlata di 2-3 frasi, diretta e colloquiale.
- **Punti chiave:** Elenco puntato chiaro e leggibile con i concetti principali (senza codice).
`;

const BASE_PROFILES: Record<string, AppProfile> = {
  generic: {
    id: 'generic',
    name: 'Assistente Generico',
    icon: 'Bot',
    description: 'Un assistente neutro per qualsiasi conversazione o riunione.',
    contextText: 'Assistente generico pronto all\'uso.',
    systemPrompt: GENERIC_PROMPT,
    keyterms: []
  },
  dev: {
    id: 'dev',
    name: 'Sviluppatore Software',
    icon: 'Terminal',
    description: 'Consulente tecnico per developer e technical interviews.',
    contextText: 'Modalità Developer Interview attiva.',
    keyterms: TECH_KEYTERMS,
    dictionaryDomains: [
      'java', 'js', 'ts', 'python', 'node', 'react', 'spring', 'db', 'devops',
      'cloud', 'architecture', 'protocols', 'tools', 'git', 'testing',
      'containers', 'messaging', 'aws', 'cloudflare', 'dotnet', 'css', 'ai',
      'security', 'auth', 'os', 'mobile'
    ],
    systemPrompt: `Sei un Consulente Tecnico Senior e Sviluppatore Full Stack di altissimo livello. Il tuo obiettivo è generare output per un teleprompter/assistente tecnico destinato a professionisti. Le tue risposte devono essere chirurgiche, prive di convenevoli e orientate alla produzione.

REGOLA FONDAMENTALE (STRICT MODE): 
Devi operare in modalità "Technical Absolute". Non sono ammesse risposte superficiali, riassunti teorici generici o omissioni nel codice.

REGOLE DI FORMATTAZIONE E CONTENUTO:
1. Regola del Codice per Singolo Punto: Nella sezione "Punti chiave", è SEVERAMENTE VIETATO fare elenchi puramente discorsivi. Per OGNI SINGOLO PUNTO in elenco, DEVI obbligatoriamente fornire un blocco di codice pratico, robusto e commentato immediatamente sotto la descrizione del punto.
2. Niente Placeholders: Tutto il codice generato deve essere di livello "God-Tier", pronto per la produzione. Non usare MAI placeholder come \`// ... codice qui\` o \`// implementazione\`. Scrivi lo script o la logica completa.
3. Tono: Autorevole, tecnico, asciutto. Vai dritto al punto.
4. Se la domanda è FUORI dal contesto professionale attuale, rispondi comunque ma segnalalo.

STRUTTURA DI OUTPUT OBBLIGATORIA (Puro Testo Markdown):
Non usare formati JSON. Rispondi direttamente con il testo, rispettando esattamente i seguenti blocchi:
- **Risposta (pronta da dire):** Un'introduzione parlata di 2-3 frasi, diretta e professionale.
- **Punti chiave:** Elenco delle feature. FORMATO TASSATIVO:
  - **[Nome Feature]**: [Spiegazione tecnica del vantaggio reale].
    \`\`\`[linguaggio]
    [Codice esaustivo per Feature]
    \`\`\`
`
  },
  tech_consultant: {
    id: 'tech_consultant',
    name: 'Tech Consultant',
    icon: 'Cloud',
    description: 'Ideale per prevendite tech, solution architecture e IT consulting.',
    contextText: 'Assistente Tech Consultant pronto.',
    keyterms: CONSULTANT_KEYTERMS,
    dictionaryDomains: ['architecture', 'cloud', 'devops', 'db', 'protocols', 'aws', 'cloudflare', 'security', 'ai', 'tools'],
    systemPrompt: buildProfilePrompt(
      'Tech Consultant / Solution Architect',
      'Concentrati su architettura, scelte tecnologiche, scalabilità, costi (TCO), trade-off e valore di business della soluzione.'
    )
  },
  sales_rep: {
    id: 'sales_rep',
    name: 'Sales Rep',
    icon: 'Briefcase',
    description: 'Supporto per vendite B2B, gestione obiezioni e closing.',
    contextText: 'Assistente Commerciale pronto.',
    keyterms: SALES_KEYTERMS,
    systemPrompt: buildProfilePrompt(
      'Sales Representative B2B',
      'Concentrati su gestione delle obiezioni, value proposition, qualificazione del lead, leve di closing e prossimi passi concreti.'
    )
  },
  marketing_freelance: {
    id: 'marketing_freelance',
    name: 'Marketing Freelance',
    icon: 'Target',
    description: 'Ottimizza le tue call con i clienti per campagne e social media.',
    contextText: 'Assistente Marketing pronto.',
    keyterms: MARKETING_KEYTERMS,
    systemPrompt: buildProfilePrompt(
      'Marketing Freelance',
      'Concentrati su campagne, contenuti, social media, metriche (CTR, conversioni) e proposte di valore per il cliente.'
    )
  },
  hr: {
    id: 'hr',
    name: 'Recruiter (HR)',
    icon: 'Users',
    description: 'Gestisci colloqui conoscitivi e selezioni di personale.',
    contextText: 'Assistente HR pronto.',
    keyterms: HR_KEYTERMS,
    systemPrompt: buildProfilePrompt(
      'Recruiter / HR',
      'Concentrati su domande comportamentali, valutazione di soft skill e competenze, fit culturale e conduzione del colloquio. Evita bias e domande non consentite.'
    )
  },
  marketing: {
    id: 'marketing',
    name: 'Marketing Manager',
    icon: 'Target',
    description: 'Per direzioni marketing, budget e strategie di crescita.',
    contextText: 'Assistente Marketing Manager pronto.',
    keyterms: MARKETING_KEYTERMS,
    systemPrompt: buildProfilePrompt(
      'Marketing Manager',
      'Concentrati su strategia, budget, posizionamento, funnel di crescita e KPI di marketing.'
    )
  },
  design: {
    id: 'design',
    name: 'Product Designer',
    icon: 'PenTool',
    description: 'Conduci interviste UX e design review con precisione.',
    contextText: 'Assistente Design pronto.',
    keyterms: DESIGN_KEYTERMS,
    systemPrompt: buildProfilePrompt(
      'Product Designer (UX/UI)',
      'Concentrati su user research, usabilità, accessibilità, design system e motivazione delle scelte di design.'
    )
  },
  cyber: {
    id: 'cyber',
    name: 'Cybersecurity Analyst',
    icon: 'Shield',
    description: 'Per audit, compliance e incident response.',
    contextText: 'Assistente Cyber pronto.',
    keyterms: CYBER_KEYTERMS,
    dictionaryDomains: ['security', 'auth', 'protocols', 'os', 'devops', 'cloud'],
    systemPrompt: buildProfilePrompt(
      'Cybersecurity Analyst',
      'Concentrati su vulnerabilità, threat model, incident response, compliance e best practice di sicurezza (defense-in-depth, zero trust).'
    )
  },
  data: {
    id: 'data',
    name: 'Data Scientist',
    icon: 'Database',
    description: 'Supporto in algoritmi, machine learning e analisi dati.',
    contextText: 'Assistente Data pronto.',
    keyterms: DATA_KEYTERMS,
    dictionaryDomains: ['ai', 'db', 'python', 'cloud', 'tools'],
    systemPrompt: buildProfilePrompt(
      'Data Scientist',
      'Concentrati su scelta del modello, feature engineering, metriche di valutazione, trade-off bias/varianza e interpretazione dei risultati. Puoi citare formule o pseudo-codice solo se richiesto.'
    )
  },
  pm: {
    id: 'pm',
    name: 'Product Manager',
    icon: 'Briefcase',
    description: 'Gestione roadmap, stakeholder e sprint review.',
    contextText: 'Assistente PM pronto.',
    keyterms: PM_KEYTERMS,
    systemPrompt: buildProfilePrompt(
      'Product Manager',
      'Concentrati su prioritizzazione, roadmap, gestione degli stakeholder, metriche di prodotto (KPI/OKR) e definizione dell\'MVP.'
    )
  }
};

export const DEFAULT_PROFILE = 'generic';

// Ricorda l'ultimo profilo persona usato: al login l'utente atterra sul suo
// profilo (sincronizzato di base) invece che sempre su /app/dev.
export const LAST_PROFILE_KEY = 'di_last_profile';

export function rememberProfile(id: string) {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LAST_PROFILE_KEY, id);
  } catch { /* ignore */ }
}

export function getLastProfileId(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const last = localStorage.getItem(LAST_PROFILE_KEY);
    return last && getAllProfiles()[last] ? last : null;
  } catch {
    return null;
  }
}

export function getDefaultAppRoute(): string {
  const last = getLastProfileId();
  return last ? `/app/${last}` : '/app/dev';
}

// Il target di un CTA (es. "Usa versione browser", "?plan=weekly") deve
// sopravvivere al giro di login: lo si persiste e lo si rilegge dopo l'auth,
// cosi' l'utente atterra DIRETTAMENTE sulla pagina voluta (non sul default).
export const POST_LOGIN_REDIRECT_KEY = 'jarvis_post_login_redirect';

export function setPostLoginRedirect(path: string) {
  try {
    if (typeof sessionStorage !== 'undefined' && path && path.startsWith('/')) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
    }
  } catch { /* ignore */ }
}

export function getPostLoginRedirect(): string | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) : null;
  } catch {
    return null;
  }
}

// Consuma il target persistito (una sola volta) e ricade sul default app route.
export function consumePostLoginRedirect(): string {
  const stored = getPostLoginRedirect();
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch { /* ignore */ }
  return stored && stored.startsWith('/') ? stored : getDefaultAppRoute();
}

export function getAllProfiles(): Record<string, AppProfile> {
  let customProfiles: Record<string, AppProfile> = {};
  try {
    const customStr = localStorage.getItem('jarvis_custom_profiles');
    if (customStr) {
      customProfiles = JSON.parse(customStr);
    }
  } catch (e) {
    console.error('Failed to parse custom profiles', e);
  }
  return { ...BASE_PROFILES, ...customProfiles };
}

export function getProfile(id?: string | null): AppProfile {
  const profiles = getAllProfiles();
  return profiles[id || DEFAULT_PROFILE] ?? profiles[DEFAULT_PROFILE] ?? Object.values(BASE_PROFILES)[0]!;
}

export function getProfileDisplayName(
  profile: AppProfile,
  t: (section: keyof TranslationKeys, key: string) => unknown
): string {
  const key = `${profile.id}.name`;
  const translated = t('profiles' as keyof TranslationKeys, key);
  return typeof translated === 'string' && translated !== key ? translated : profile.name;
}
export function saveCustomProfile(profile: AppProfile) {
  let customProfiles: Record<string, AppProfile> = {};
  try {
    const customStr = localStorage.getItem('jarvis_custom_profiles');
    if (customStr) customProfiles = JSON.parse(customStr);
  } catch {
    // ignore parse errors
  }
  
  customProfiles[profile.id] = { ...profile, isCustom: true };
  localStorage.setItem('jarvis_custom_profiles', JSON.stringify(customProfiles));
  window.dispatchEvent(new Event('profiles_updated'));
}

export function deleteCustomProfile(id: string) {
  let customProfiles: Record<string, AppProfile> = {};
  try {
    const customStr = localStorage.getItem('jarvis_custom_profiles');
    if (customStr) customProfiles = JSON.parse(customStr);
  } catch {
    // ignore parse errors
  }
  
  if (customProfiles[id]) {
    delete customProfiles[id];
    localStorage.setItem('jarvis_custom_profiles', JSON.stringify(customProfiles));
    window.dispatchEvent(new Event('profiles_updated'));
  }
}
